
import {Language} from "../../config/LanguageConfig";
import {OutgoingInvoice} from "./OutgoingInvoice";

/**
 * Represents the types of invoices that can be generated or handled by the system.
 * This helps standardize the categorization of invoices in various business workflows.
 *
 * The `InvoiceType` enumeration provides clear identifiers for different invoicing stages
 * or formats, ensuring consistent use throughout the codebase.
 *
 * @enum {number}
 */
export enum InvoiceType {
    FINAL_INVOICE = 1,
    PARTIAL_INVOICE = 2,
    SINGLE_INVOICE = 3
}
/**
 * Retrieves the localized title for a given invoice type based on its ID and language preferences.
 * This method helps ensure proper display of invoice types in different languages.
 *
 * The function maps a provided `invoiceTypeId` to a corresponding title string found within the `language` object.
 * If the provided `invoiceTypeId` does not match any predefined values, an error is thrown.
 *
 * @param invoiceTypeId - The numeric identifier representing the type of invoice (e.g., final, partial, or single).
 * @param language - The object containing localized strings, structured to provide titles for each invoice type.
 * @return The localized title of the invoice type as a string.
 * @throws Error If the provided `invoiceTypeId` does not match any defined invoice type.
 */
export function getTypeTitle( invoiceTypeId: number, language: Language ): string {


    switch( invoiceTypeId ) {
        case InvoiceType.FINAL_INVOICE:
            return language.accounting.type.finalInvoice.title;
        case InvoiceType.PARTIAL_INVOICE:
            return language.accounting.type.partialInvoice.title;
        case InvoiceType.SINGLE_INVOICE:
            return language.accounting.type.singleInvoice.title;
    }

    throw Error( "type id not found" );
}

/**
 * Retrieves the invoice type based on its numeric identifier.
 */
export function invoiceTypeFromNumber( invoiceTypeId: number ): InvoiceType {
    switch( invoiceTypeId ) {
        case InvoiceType.FINAL_INVOICE:
            return InvoiceType.FINAL_INVOICE;
        case InvoiceType.PARTIAL_INVOICE:
            return InvoiceType.PARTIAL_INVOICE;
        case InvoiceType.SINGLE_INVOICE:
            return InvoiceType.SINGLE_INVOICE;
    }

    throw Error("invoice type not found!")
}